import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { Brand } from "@/components/Brand";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";

export default function PaymentResult({ success }) {
  const [params] = useSearchParams();
  const orderId = params.get("order_id") || params.get("session_id");
  const [state, setState] = useState(success ? "polling" : "cancelled");
  const [info, setInfo] = useState(null);

  useEffect(() => {
    if (!success || !orderId) {
      if (success) setState("failed");
      return;
    }
    let attempts = 0;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${orderId}`);
        if (data.payment_status === "paid") {
          setInfo(data);
          setState("paid");
          return;
        }
      } catch {}
      attempts += 1;
      if (attempts < 6) setTimeout(poll, 2000);
      else setState("failed");
    };
    poll();
  }, [success, orderId]);

  return (
    <div className="min-h-screen navy-bg noise-overlay flex items-center justify-center p-6">
      <div className="bg-white rounded-2xl p-10 max-w-md w-full text-center relative z-10" data-testid="payment-result">
        <div className="flex justify-center mb-8"><Brand /></div>
        {state === "polling" && (
          <div data-testid="payment-confirming">
            <Loader2 className="w-12 h-12 text-[#2563EB] animate-spin mx-auto" />
            <h1 className="font-heading font-bold text-xl mt-6">Confirming your payment…</h1>
            <p className="text-sm text-muted-foreground mt-2">This takes a few seconds.</p>
          </div>
        )}
        {state === "paid" && (
          <>
            <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto" />
            <h1 className="font-heading font-bold text-xl mt-6" data-testid="payment-success-title">Payment successful</h1>
            <p className="text-sm text-muted-foreground mt-2">{info?.description || "Your purchase is confirmed."}</p>
            <Link to={info?.kind === "course" ? "/candidate/learning" : (info?.kind === "franchise" || info?.role === "franchise_owner") ? "/partner" : "/employer"}>
              <Button className="mt-8 rounded-full bg-[#0B1B3D] hover:bg-[#13264f] w-full" data-testid="payment-continue-btn">
                Continue to Dashboard
              </Button>
            </Link>
          </>
        )}
        {state === "failed" && (
          <>
            <XCircle className="w-12 h-12 text-amber-500 mx-auto" />
            <h1 className="font-heading font-bold text-xl mt-6">Payment not confirmed yet</h1>
            <p className="text-sm text-muted-foreground mt-2">
              If you completed the payment, it will reflect shortly. Check your billing page.
            </p>
            <Link to="/employer/billing">
              <Button className="mt-8 rounded-full bg-[#0B1B3D] w-full" data-testid="payment-billing-btn">Go to Billing</Button>
            </Link>
          </>
        )}
        {state === "cancelled" && (
          <>
            <XCircle className="w-12 h-12 text-slate-400 mx-auto" />
            <h1 className="font-heading font-bold text-xl mt-6" data-testid="payment-cancelled-title">Payment cancelled</h1>
            <p className="text-sm text-muted-foreground mt-2">No amount was charged. You can retry anytime.</p>
            <Link to="/employer/plans">
              <Button className="mt-8 rounded-full bg-[#0B1B3D] w-full" data-testid="payment-retry-btn">Back to Plans</Button>
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
